import { useState } from "react";

interface ChartData {
  title: string;
  misleadingLabel: string;
  honestLabel: string;
  values: { label: string; value: number }[];
  /** Optional second set of values for honest view (e.g. Simpson's Paradox) */
  honestValues?: { label: string; value: number; color?: string }[];
  yMin?: number;
  yMax?: number;
  unit?: string;
  explanation: string;
  /** Custom chart type */
  chartType?: "bar" | "mean-median" | "simpsons" | "probability" | "anchor";
}

const chartDataByFallacy: Record<string, ChartData> = {
  "misleading-statistics": {
    title: "Pertumbuhan Ekonomi Daerah",
    misleadingLabel: "Grafik Menyesatkan (Y: 5.1%–5.4%)",
    honestLabel: "Grafik Jujur (Y: 0%–6%)",
    values: [
      { label: "2021", value: 5.1 },
      { label: "2022", value: 5.15 },
      { label: "2023", value: 5.25 },
      { label: "2024", value: 5.4 },
    ],
    yMin: 5.0,
    yMax: 5.5,
    unit: "%",
    explanation:
      "Grafik kiri memotong sumbu Y dari 5.0% sehingga kenaikan 0.3% terlihat meroket. Grafik kanan menunjukkan skala penuh — garisnya hampir datar.",
  },
  "misleading-percentages": {
    title: "Pertumbuhan Pengguna Startup",
    misleadingLabel: "\"Naik 500%!\" (Persentase)",
    honestLabel: "Angka Sebenarnya (Absolut)",
    values: [
      { label: "Jan", value: 10 },
      { label: "Feb", value: 18 },
      { label: "Mar", value: 35 },
      { label: "Apr", value: 60 },
    ],
    unit: " user",
    explanation:
      "\"Growth 500%\" terdengar fantastis, tapi dari 10 user jadi 60 user. Selalu tanya angka absolutnya!",
  },
  "mean-vs-median": {
    title: "Gaji Karyawan PT Sukses Makmur",
    misleadingLabel: "Rata-rata (Mean): Rp33.6jt",
    honestLabel: "Median: Rp5jt",
    chartType: "mean-median",
    values: [
      { label: "Staff 1", value: 4 },
      { label: "Staff 2", value: 4.5 },
      { label: "Staff 3", value: 5 },
      { label: "Staff 4", value: 5 },
      { label: "Staff 5", value: 5.5 },
      { label: "Manager", value: 12 },
      { label: "Direktur", value: 200 },
    ],
    unit: "jt",
    explanation:
      "Gaji direktur Rp200jt menarik rata-rata ke Rp33.6jt, padahal mayoritas karyawan hanya bergaji Rp4–5.5jt. Median (Rp5jt) jauh lebih jujur.",
  },
  "relative-vs-absolute-risk": {
    title: "Risiko Penyakit dari Makanan Olahan",
    misleadingLabel: "\"Naik 300%!\" (Risiko Relatif)",
    honestLabel: "Angka Sebenarnya (Risiko Absolut)",
    values: [
      { label: "Tanpa\nolahan", value: 0.01 },
      { label: "Dengan\nolahan", value: 0.04 },
    ],
    unit: "%",
    explanation:
      "\"Naik 300%\" terdengar mengerikan, tapi risikonya naik dari 0.01% ke 0.04%. Secara absolut, peningkatannya hanya 0.03 persen poin.",
  },
  "simpsons-paradox": {
    title: "Tingkat Kematian Rumah Sakit",
    misleadingLabel: "Data Agregat (Keseluruhan)",
    honestLabel: "Data per Kategori Operasi",
    chartType: "simpsons",
    values: [
      { label: "RS\nHarapan", value: 5 },
      { label: "RS\nSentosa", value: 3 },
    ],
    unit: "%",
    explanation:
      "RS Harapan terlihat lebih buruk (5% vs 3%), tapi 80% pasiennya adalah kasus berat. Per kategori, RS Harapan justru lebih baik di KEDUA jenis operasi!",
  },
  "survivorship-bias": {
    title: "Kisah Sukses Dropout Kuliah",
    misleadingLabel: "\"Dropout = Sukses!\"",
    honestLabel: "Data Lengkap",
    values: [
      { label: "Bill\nGates", value: 130 },
      { label: "Mark\nZuck", value: 100 },
      { label: "Steve\nJobs", value: 80 },
    ],
    honestValues: [
      { label: "Bill\nGates", value: 130, color: "#22c55e" },
      { label: "Mark\nZuck", value: 100, color: "#22c55e" },
      { label: "Steve\nJobs", value: 80, color: "#22c55e" },
      { label: "Dropout\nA", value: 4 },
      { label: "Dropout\nB", value: 3 },
      { label: "Dropout\nC", value: 5 },
      { label: "Dropout\nD", value: 2 },
      { label: "Dropout\nE", value: 6 },
      { label: "Dropout\nF", value: 3 },
      { label: "Dropout\nG", value: 4 },
    ],
    unit: "M$",
    explanation:
      "Media hanya menunjukkan 3 dropout yang sukses besar. Ribuan dropout lainnya bergaji biasa atau menganggur. Kamu hanya melihat yang selamat.",
  },
  "base-rate-fallacy": {
    title: "Tes Penyakit Langka (Akurasi 99%)",
    misleadingLabel: "\"Positif = Pasti Sakit!\"",
    honestLabel: "Hitung Pakai Base Rate",
    chartType: "bar",
    values: [
      { label: "Benar\nPositif", value: 99 },
      { label: "Salah\nPositif", value: 0 },
    ],
    honestValues: [
      { label: "Benar\nPositif", value: 99, color: "#ef4444" },
      { label: "Salah\nPositif", value: 999, color: "#f97316" },
    ],
    unit: " orang",
    explanation:
      "Dari 100.000 orang, hanya 100 sakit (0.1%). Tes tangkap 99 orang sakit, TAPI juga salah deteksi 999 orang sehat. Jadi kalau positif, peluang benar sakit hanya 9%!",
  },
  "gamblers-fallacy": {
    title: "Lemparan Koin: Sudah 5× Muncul Angka",
    misleadingLabel: "\"Pasti Gambar Berikutnya!\"",
    honestLabel: "Peluang Sebenarnya",
    chartType: "probability",
    values: [
      { label: "Lempar\n1", value: 50 },
      { label: "Lempar\n2", value: 50 },
      { label: "Lempar\n3", value: 50 },
      { label: "Lempar\n4", value: 50 },
      { label: "Lempar\n5", value: 50 },
      { label: "Lempar\n6", value: 50 },
    ],
    unit: "%",
    explanation:
      "Setiap lemparan koin bersifat INDEPENDEN. Tidak peduli sebelumnya keluar apa, peluang berikutnya tetap 50:50. Koin tidak punya memori!",
  },
  "sampling-bias": {
    title: "Survei \"Brand Favorit Indonesia\"",
    misleadingLabel: "Survei di Mall Jakarta",
    honestLabel: "Survei Nasional (Random)",
    values: [
      { label: "Brand\nMewah", value: 72 },
      { label: "Brand\nLokal", value: 18 },
      { label: "Lainnya", value: 10 },
    ],
    honestValues: [
      { label: "Brand\nMewah", value: 15 },
      { label: "Brand\nLokal", value: 58 },
      { label: "Lainnya", value: 27 },
    ],
    unit: "%",
    explanation:
      "Survei di mall Jakarta menunjukkan 72% suka brand mewah. Survei nasional acak? Hanya 15%. Sampel menentukan hasil — tanyakan selalu: siapa yang disurvei?",
  },
  "texas-sharpshooter": {
    title: "Penjualan Es Krim vs Kasus Tenggelam",
    misleadingLabel: "\"Es Krim Sebabkan Tenggelam!\"",
    honestLabel: "Lihat Variabel Tersembunyi",
    values: [
      { label: "Jan", value: 12 },
      { label: "Mar", value: 25 },
      { label: "Mei", value: 48 },
      { label: "Jul", value: 65 },
      { label: "Sep", value: 40 },
      { label: "Nov", value: 15 },
    ],
    honestValues: [
      { label: "Jan", value: 12 },
      { label: "Mar", value: 25 },
      { label: "Mei", value: 48 },
      { label: "Jul", value: 65 },
      { label: "Sep", value: 40 },
      { label: "Nov", value: 15 },
    ],
    unit: "",
    explanation:
      "Penjualan es krim dan kasus tenggelam sama-sama naik di musim panas — tapi es krim tidak menyebabkan tenggelam. Variabel tersembunyinya: CUACA PANAS. Korelasi ≠ kausalitas!",
  },
  "ecological-fallacy": {
    title: "Rata-rata Gaji per Provinsi",
    misleadingLabel: "\"Provinsi X Kaya Semua!\"",
    honestLabel: "Gaji Individu di Provinsi X",
    values: [
      { label: "Prov A", value: 4.2 },
      { label: "Prov B", value: 5.8 },
      { label: "Prov X", value: 12.5 },
      { label: "Prov C", value: 3.8 },
    ],
    honestValues: [
      { label: "Petani", value: 2.5 },
      { label: "Guru", value: 4 },
      { label: "Buruh", value: 3.5 },
      { label: "Perawat", value: 5 },
      { label: "PNS", value: 7 },
      { label: "Eksekutif\nTambang", value: 85 },
    ],
    unit: "jt",
    explanation:
      "Rata-rata gaji Provinsi X tinggi (12.5jt) karena industri tambang. Tapi mayoritas warganya bergaji 2.5–7jt. Statistik kelompok tidak berlaku untuk individu!",
  },
  "anchoring-bias": {
    title: "Harga Headphone di Toko Online",
    misleadingLabel: "\"Diskon 90%! Dari Rp5jt!\"",
    honestLabel: "Bandingkan Harga Pasar",
    chartType: "anchor",
    values: [
      { label: "\"Harga\nAsli\"", value: 5000 },
      { label: "Harga\nPromo", value: 500 },
    ],
    honestValues: [
      { label: "Harga\nPasar", value: 600 },
      { label: "Harga\nPromo", value: 500 },
    ],
    unit: "rb",
    explanation:
      "\"Harga asli\" Rp5jt itu fiktif — tidak pernah dijual segitu. Harga pasar sebenarnya Rp600rb, jadi \"diskon\" cuma 17%, bukan 90%! Jangan terjebak angka jangkar.",
  },
};

/* ─── Bar Chart (generic) ─── */
function BarChart({
  values,
  truncated,
  yMin,
  yMax,
  unit,
  color,
}: {
  values: { label: string; value: number; color?: string }[];
  truncated: boolean;
  yMin?: number;
  yMax?: number;
  unit?: string;
  color: string;
}) {
  const maxVal = Math.max(...values.map((v) => v.value));
  const effectiveMin = truncated && yMin !== undefined ? yMin : 0;
  const effectiveMax = truncated && yMax !== undefined ? yMax : maxVal * 1.2;
  const range = effectiveMax - effectiveMin;

  const chartH = 120;
  const barWidth = Math.min(32, Math.floor(200 / values.length));
  const gap = Math.min(12, Math.floor(80 / values.length));
  const chartW = values.length * (barWidth + gap) - gap + 44;

  return (
    <svg
      viewBox={`0 0 ${chartW} ${chartH + 40}`}
      style={{ width: "100%", maxWidth: Math.max(chartW, 200), height: "auto", fontFamily: "var(--font-body)" }}
    >
      <line x1={30} y1={5} x2={30} y2={chartH + 5} stroke="#cbd5e1" strokeWidth={1} />
      <line x1={30} y1={chartH + 5} x2={chartW} y2={chartH + 5} stroke="#cbd5e1" strokeWidth={1} />
      <text x={26} y={chartH + 5} textAnchor="end" fontSize={8} fill="#94a3b8">
        {effectiveMin}{unit}
      </text>
      <text x={26} y={10} textAnchor="end" fontSize={8} fill="#94a3b8">
        {truncated ? effectiveMax.toFixed(1) : Math.ceil(effectiveMax)}{unit}
      </text>
      {truncated && yMin !== undefined && yMin > 0 && (
        <>
          <line x1={26} y1={chartH} x2={34} y2={chartH - 3} stroke="#94a3b8" strokeWidth={1} />
          <line x1={26} y1={chartH - 3} x2={34} y2={chartH - 6} stroke="#94a3b8" strokeWidth={1} />
        </>
      )}
      {values.map((v, i) => {
        const barH = range > 0 ? ((v.value - effectiveMin) / range) * chartH : 0;
        const x = 36 + i * (barWidth + gap);
        const y = chartH + 5 - barH;
        return (
          <g key={i}>
            <rect
              x={x} y={y} width={barWidth} height={Math.max(barH, 1)} rx={3}
              fill={v.color || color} opacity={0.85}
            />
            <text x={x + barWidth / 2} y={y - 4} textAnchor="middle" fontSize={7} fill="#475569" fontWeight={600}>
              {v.value}{unit}
            </text>
            {v.label.split("\n").map((line, li) => (
              <text key={li} x={x + barWidth / 2} y={chartH + 16 + li * 10} textAnchor="middle" fontSize={7} fill="#64748b">
                {line}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Simpson's Paradox Chart ─── */
function SimpsonsChart({ showHonest }: { showHonest: boolean }) {
  const chartH = 120;
  const barW = 28;

  if (!showHonest) {
    // Aggregate view: RS Harapan 5%, RS Sentosa 3%
    const agg = [
      { label: "RS Harapan", value: 5, color: "#ef4444" },
      { label: "RS Sentosa", value: 3, color: "#3b82f6" },
    ];
    const maxV = 8;
    const w = 160;
    return (
      <svg viewBox={`0 0 ${w} ${chartH + 30}`} style={{ width: "100%", maxWidth: w, height: "auto", fontFamily: "var(--font-body)" }}>
        {agg.map((h, i) => {
          const barH = (h.value / maxV) * chartH;
          const x = 30 + i * 65;
          const y = chartH + 5 - barH;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} rx={3} fill={h.color} opacity={0.8} />
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={8} fill="#475569" fontWeight={700}>{h.value}%</text>
              <text x={x + barW / 2} y={chartH + 18} textAnchor="middle" fontSize={7} fill="#64748b">{h.label}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  // Disaggregated view: per category
  const categories = [
    {
      name: "Operasi Ringan",
      harapan: 1, sentosa: 2,
    },
    {
      name: "Operasi Berat",
      harapan: 8, sentosa: 12,
    },
  ];
  const maxV = 14;
  const w = 280;
  const groupW = 120;

  return (
    <svg viewBox={`0 0 ${w} ${chartH + 50}`} style={{ width: "100%", maxWidth: w, height: "auto", fontFamily: "var(--font-body)" }}>
      {categories.map((cat, ci) => {
        const gx = 20 + ci * groupW + ci * 20;
        return (
          <g key={ci}>
            <text x={gx + groupW / 2} y={chartH + 42} textAnchor="middle" fontSize={8} fill="#475569" fontWeight={600}>
              {cat.name}
            </text>
            {/* Harapan bar */}
            {(() => {
              const barH = (cat.harapan / maxV) * chartH;
              const x = gx + 10;
              const y = chartH + 5 - barH;
              return (
                <g>
                  <rect x={x} y={y} width={barW} height={barH} rx={3} fill="#ef4444" opacity={0.8} />
                  <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={8} fill="#475569" fontWeight={700}>{cat.harapan}%</text>
                  <text x={x + barW / 2} y={chartH + 18} textAnchor="middle" fontSize={6} fill="#ef4444">Harapan</text>
                </g>
              );
            })()}
            {/* Sentosa bar */}
            {(() => {
              const barH = (cat.sentosa / maxV) * chartH;
              const x = gx + 10 + barW + 12;
              const y = chartH + 5 - barH;
              return (
                <g>
                  <rect x={x} y={y} width={barW} height={barH} rx={3} fill="#3b82f6" opacity={0.8} />
                  <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={8} fill="#475569" fontWeight={700}>{cat.sentosa}%</text>
                  <text x={x + barW / 2} y={chartH + 18} textAnchor="middle" fontSize={6} fill="#3b82f6">Sentosa</text>
                </g>
              );
            })()}
          </g>
        );
      })}
      {/* Legend note */}
      <text x={w / 2} y={chartH + 54} textAnchor="middle" fontSize={6} fill="#94a3b8">
        RS Harapan lebih rendah di kedua kategori!
      </text>
    </svg>
  );
}

/* ─── Gambler's Fallacy Chart ─── */
function ProbabilityChart({ showHonest }: { showHonest: boolean }) {
  const chartH = 100;
  const w = 260;
  const results = ["A", "A", "A", "A", "A", "?"];
  const labels = ["1", "2", "3", "4", "5", "6"];

  return (
    <svg viewBox={`0 0 ${w} ${chartH + 50}`} style={{ width: "100%", maxWidth: w, height: "auto", fontFamily: "var(--font-body)" }}>
      {results.map((r, i) => {
        const x = 20 + i * 40;
        const isLast = i === results.length - 1;
        const coinColor = r === "A" ? "#ef4444" : "#3b82f6";
        return (
          <g key={i}>
            {/* Coin */}
            <circle
              cx={x + 15} cy={30} r={14}
              fill={isLast ? (showHonest ? "#94a3b8" : "#3b82f6") : coinColor}
              opacity={0.85}
            />
            <text x={x + 15} y={34} textAnchor="middle" fontSize={10} fill="white" fontWeight={700}>
              {isLast ? (showHonest ? "?" : "G") : r}
            </text>
            {/* Label */}
            <text x={x + 15} y={56} textAnchor="middle" fontSize={7} fill="#64748b">
              #{labels[i]}
            </text>
            {/* Probability bar below */}
            <rect x={x + 5} y={68} width={20} height={showHonest ? 30 : (isLast ? 45 : 30)} rx={2}
              fill={isLast && !showHonest ? "#3b82f6" : "#94a3b8"} opacity={0.6} />
            <text x={x + 15} y={65} textAnchor="middle" fontSize={7} fill="#475569" fontWeight={600}>
              {showHonest ? "50%" : (isLast ? "90%?" : "50%")}
            </text>
          </g>
        );
      })}
      {/* Arrow annotation for misleading */}
      {!showHonest && (
        <text x={w / 2} y={chartH + 42} textAnchor="middle" fontSize={7} fill="#ef4444" fontWeight={600}>
          "Sudah 5× Angka, pasti Gambar!" ← SALAH
        </text>
      )}
      {showHonest && (
        <text x={w / 2} y={chartH + 42} textAnchor="middle" fontSize={7} fill="#22c55e" fontWeight={600}>
          Setiap lemparan tetap 50:50 — koin tidak punya memori
        </text>
      )}
    </svg>
  );
}

/* ─── Anchor Chart ─── */
function AnchorChart({ showHonest }: { showHonest: boolean }) {
  const chartH = 110;
  const barW = 50;
  const w = 200;

  const bars = showHonest
    ? [
        { label: "Harga Pasar", value: 600, color: "#64748b" },
        { label: "Harga Promo", value: 500, color: "#22c55e" },
      ]
    : [
        { label: "\"Harga Asli\"", value: 5000, color: "#ef4444" },
        { label: "Harga Promo", value: 500, color: "#22c55e" },
      ];

  const maxV = Math.max(...bars.map((b) => b.value)) * 1.15;

  return (
    <svg viewBox={`0 0 ${w} ${chartH + 40}`} style={{ width: "100%", maxWidth: w, height: "auto", fontFamily: "var(--font-body)" }}>
      {bars.map((b, i) => {
        const barH = (b.value / maxV) * chartH;
        const x = 30 + i * (barW + 30);
        const y = chartH + 5 - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} rx={4} fill={b.color} opacity={0.85} />
            <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize={9} fill="#475569" fontWeight={700}>
              Rp{b.value >= 1000 ? `${b.value / 1000}jt` : `${b.value}rb`}
            </text>
            <text x={x + barW / 2} y={chartH + 20} textAnchor="middle" fontSize={7} fill="#64748b">
              {b.label}
            </text>
          </g>
        );
      })}
      {!showHonest && (
        <text x={w / 2} y={chartH + 35} textAnchor="middle" fontSize={8} fill="#ef4444" fontWeight={700}>
          "DISKON 90%!"
        </text>
      )}
      {showHonest && (
        <text x={w / 2} y={chartH + 35} textAnchor="middle" fontSize={8} fill="#22c55e" fontWeight={700}>
          Diskon sebenarnya: ~17%
        </text>
      )}
    </svg>
  );
}

/* ─── Mean vs Median Chart ─── */
function MeanMedianChart({
  data,
  showHonest,
}: {
  data: ChartData;
  showHonest: boolean;
}) {
  const values = data.values;
  const maxVal = Math.max(...values.map((v) => v.value));
  const chartH = 120;
  const barWidth = 20;
  const gap = 4;
  const chartW = values.length * (barWidth + gap) - gap + 60;

  const mean = values.reduce((a, v) => a + v.value, 0) / values.length;
  const sorted = [...values].sort((a, b) => a.value - b.value);
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1].value + sorted[sorted.length / 2].value) / 2
      : sorted[Math.floor(sorted.length / 2)].value;

  const scale = chartH / (maxVal * 1.1);

  return (
    <svg
      viewBox={`0 0 ${chartW} ${chartH + 45}`}
      style={{ width: "100%", maxWidth: chartW, height: "auto", fontFamily: "var(--font-body)" }}
    >
      {values.map((v, i) => {
        const barH = v.value * scale;
        const x = 36 + i * (barWidth + gap);
        const y = chartH + 5 - barH;
        const isOutlier = v.value > mean * 2;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={Math.max(barH, 1)} rx={2}
              fill={isOutlier && showHonest ? "#f97316" : "#64748b"} opacity={0.75} />
            <text x={x + barWidth / 2} y={y - 3} textAnchor="middle" fontSize={6} fill="#475569" fontWeight={600}>
              {v.value}{data.unit}
            </text>
            {v.label.split("\n").map((line, li) => (
              <text key={li} x={x + barWidth / 2} y={chartH + 16 + li * 9} textAnchor="middle" fontSize={6} fill="#64748b">
                {line}
              </text>
            ))}
          </g>
        );
      })}
      {!showHonest && (
        <>
          <line x1={30} y1={chartH + 5 - mean * scale} x2={chartW - 4} y2={chartH + 5 - mean * scale}
            stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4,3" />
          <text x={chartW - 2} y={chartH + 2 - mean * scale} textAnchor="end" fontSize={7} fill="#ef4444" fontWeight={700}>
            Mean: {mean.toFixed(1)}jt
          </text>
        </>
      )}
      {showHonest && (
        <>
          <line x1={30} y1={chartH + 5 - median * scale} x2={chartW - 4} y2={chartH + 5 - median * scale}
            stroke="#22c55e" strokeWidth={1.5} strokeDasharray="4,3" />
          <text x={chartW - 2} y={chartH + 2 - median * scale} textAnchor="end" fontSize={7} fill="#22c55e" fontWeight={700}>
            Median: {median}jt
          </text>
          <line x1={30} y1={chartH + 5 - mean * scale} x2={chartW - 4} y2={chartH + 5 - mean * scale}
            stroke="#ef4444" strokeWidth={1} strokeDasharray="2,3" opacity={0.5} />
          <text x={chartW - 2} y={chartH - 2 - mean * scale} textAnchor="end" fontSize={6} fill="#ef4444" opacity={0.6}>
            Mean: {mean.toFixed(1)}jt
          </text>
        </>
      )}
    </svg>
  );
}

/* ─── Main StatChart Component ─── */
export function StatChart({ fallacyId }: { fallacyId: string }) {
  const data = chartDataByFallacy[fallacyId];
  if (!data) return null;

  const [showHonest, setShowHonest] = useState(false);

  function renderChart() {
    const chartType = data.chartType || "bar";

    if (chartType === "mean-median") {
      return <MeanMedianChart data={data} showHonest={showHonest} />;
    }
    if (chartType === "simpsons") {
      return <SimpsonsChart showHonest={showHonest} />;
    }
    if (chartType === "probability") {
      return <ProbabilityChart showHonest={showHonest} />;
    }
    if (chartType === "anchor") {
      return <AnchorChart showHonest={showHonest} />;
    }

    // Default bar chart — use honestValues if available
    const values = showHonest && data.honestValues ? data.honestValues : data.values;
    return (
      <BarChart
        values={values}
        truncated={!showHonest}
        yMin={data.yMin}
        yMax={data.yMax}
        unit={data.unit}
        color={showHonest ? "#22c55e" : "#ef4444"}
      />
    );
  }

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 16,
      }}
    >
      <p
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: 14,
          fontWeight: 700,
          color: "#0f172a",
          marginBottom: 4,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 16 }}>📊</span> {data.title}
      </p>

      {/* Toggle */}
      <div
        style={{
          display: "flex",
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          marginBottom: 12,
          marginTop: 10,
        }}
      >
        <button
          onClick={() => setShowHonest(false)}
          style={{
            flex: 1,
            padding: "8px 6px",
            fontFamily: "var(--font-body)",
            fontSize: 11,
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            backgroundColor: !showHonest ? "#fee2e2" : "#f8fafc",
            color: !showHonest ? "#991b1b" : "#64748b",
            transition: "all 0.2s",
          }}
        >
          ❌ {data.misleadingLabel}
        </button>
        <button
          onClick={() => setShowHonest(true)}
          style={{
            flex: 1,
            padding: "8px 6px",
            fontFamily: "var(--font-body)",
            fontSize: 11,
            fontWeight: 600,
            border: "none",
            borderLeft: "1px solid #e2e8f0",
            cursor: "pointer",
            backgroundColor: showHonest ? "#dcfce7" : "#f8fafc",
            color: showHonest ? "#166534" : "#64748b",
            transition: "all 0.2s",
          }}
        >
          ✅ {data.honestLabel}
        </button>
      </div>

      {/* Chart area */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "8px 0",
          minHeight: 140,
        }}
      >
        {renderChart()}
      </div>

      {/* Explanation */}
      <div
        style={{
          backgroundColor: showHonest ? "#f0fdf4" : "#fef2f2",
          borderRadius: 8,
          padding: "10px 12px",
          marginTop: 8,
          transition: "background-color 0.2s",
        }}
      >
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 13,
            color: showHonest ? "#166534" : "#991b1b",
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          {data.explanation}
        </p>
      </div>
    </div>
  );
}
